<template>
  <div class="app-signup-top-pane">
    <sub-header-section :pageTitle="$tc('LoginPage.signup')" backLink="/login" />

    <div class="app-signup-top-pane__main">
      <p class="app-signup-top-pane__title">{{ $tc('SignupPage.IndexPage.registerWithEmail') }}</p>

      <div class="app-signup-top-pane__input">
        <mail-input-section
          :isSendingMail="state.isSendingMail"
          :hasMessage="false"
          :isUseReCaptcha="false"
          @formSubmit="onFormSubmit"
        />
      </div>

      <div class="app-signup-top-pane__services">
        <p class="app-signup-top-pane__services__title">
          {{ $tc('SignupPage.IndexPage.registerUsingService') }}
        </p>

        <div class="app-signup-top-pane__services__list">
          <service-button-list-parts
            @onStartGoogleOauthFlow="startGoogleOauthFlow"
            @onStartAppleOauthFlow="startAppleOauthFlow"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive } from '@vue/composition-api'
import SubHeaderSection from '@/components/common/Header/SubHeaderSection.vue'
import MailInputSection from '@/components/SignupPage/EmailAuthenticationPane/MailInputSection.vue'
import ServiceButtonListParts from '@/components/AppSignupPage/AppSignupTopPane/parts/ServiceButtonListParts.vue'
import useUser from '@/store/hook/useUser'
import Const from '@/util/Const'
import MessageDialogStore from '@/store/stores/pageStore/common/MessageDialogStore'
import OAuthCodeFlowManager from '@/util/oauth/OAuthCodeFlowManager'
import useAppleSignInFlow from '@/components/AppSignupPage/hook/useAppleSignInFlow'

/**
 * アプリ新規会員登録: 新規会員登録ペインのコンポーネント
 */
export default defineComponent({
  name: 'AppSignupTopPane',
  components: { ServiceButtonListParts, MailInputSection, SubHeaderSection },
  setup() {
    const { appleOauthFlow } = useAppleSignInFlow()
    const { registerToken } = useUser()

    const state = reactive({
      isSendingMail: false,
    })

    /**
     * Appleの認可を開始する
     */
    const startAppleOauthFlow = () => appleOauthFlow()

    return {
      // AppSignupTopPane
      state,
      startAppleOauthFlow,
      // useUser
      registerToken,
    }
  },
  methods: {
    async onFormSubmit(email: string) {
      this.state.isSendingMail = true
      const loader = this.$loading.show()
      // ユーザー登録ページのURLに付与するトークンを生成し、入力したメールアドレス宛てにメール送信
      const result = await this.registerToken(Const.MEMBER_TYPE.FREE, 'MAIL_ADDRESS', email)
      this.state.isSendingMail = false
      loader.hide()
      if (!result.isSuccess) {
        await MessageDialogStore.value.open({
          title: this.$tc('SignupPage.common.errors.generateTokenError.title'),
          message: this.$tc('SignupPage.common.errors.generateTokenError.message'),
          errorApiResponse: result?.response,
        })
        return
      }

      // ボタンが押下されたことを通知する
      this.$emit('formSubmit')
    },
    /**
     * Googleの認可を開始する
     */
    async startGoogleOauthFlow() {
      OAuthCodeFlowManager.createOAuthCodeFlowManager('google')
      const oAuthCodeFlowManager = OAuthCodeFlowManager.getOAuthCodeFlowManager()
      oAuthCodeFlowManager?.startOauthCodeFlow(['profile', 'email'])
    },
  },
})
</script>

<style scoped lang="scss">
.app-signup-top-pane {
  min-height: calc(100svh - 48px);
  color: $gray75;
  background-color: $gray1;

  &__main {
    max-width: 480px;
    padding: 80px 0;
    margin: 0 auto;
    text-align: center;
  }

  &__title {
    font-size: 17px;
    font-weight: 500;
  }

  &__input {
    padding: 0 16px;
    margin-top: 24px;

    .mail-input-section {
      padding: 0;
    }
  }

  &__services {
    padding: 24px 16px 0;
    margin-top: 24px;
    border-top: 1px solid $black10;

    &__title {
      font-size: 17px;
      font-weight: 500;
    }

    &__list {
      margin-top: 24px;
    }
  }
}
</style>
